import { Link } from "react-router-dom"
import allfcLightLogo from "@/assets/logos/allfc-light.svg"
import Loading from "@/components/common/Loading"
import HomeLogoMarquee from "@/components/home/HomeLogoMarquee"
import { useTournamentList } from "@/domain/tournament/hooks/TournamentListHook"
import type { TournamentListItem } from "@/domain/tournament/model/Tournament"

/** 예정 대회 — 접수 중인 대회를 먼저, 없으면 목록의 첫 대회를 보여준다 */
function findFeaturedTournament(
  tournaments: TournamentListItem[],
): TournamentListItem | null {
  const openTournament = tournaments.find(
    (tournament) => tournament.isEntryOpen,
  )

  return openTournament ?? tournaments[0] ?? null
}

export default function HomePage() {
  const { useTournamentListState } = useTournamentList()
  const { tournaments, isLoaded, errorMessage } = useTournamentListState()

  const featuredTournament = findFeaturedTournament(tournaments)
  const openTournamentCount = tournaments.filter(
    (tournament) => tournament.isEntryOpen,
  ).length
  const homeStats = [
    { value: String(tournaments.length), label: "등록된 대회" },
    { value: String(openTournamentCount), label: "접수 중 대회" },
  ]

  return (
    <>
      {/* 히어로 — 블랙 면 위에 ALLFC 로고(흰색 버전)와 문구만 얹는다 */}
      <section className="bg-app-secondary-900 text-app-text-100">
        <div className="app-page">
          <p className="app-txt-eyebrow text-app-primary-400">
            FIGHT SPORTS TOURNAMENT PLATFORM
          </p>
          <h1 className="mt-5">
            {/* 로고 SVG 안쪽 좌측 여백(폭의 7.7%)만큼 당겨 아래 문구와 왼쪽을 맞춘다 */}
            <img
              src={allfcLightLogo}
              alt="ALLFC"
              className="-ml-5 h-32 w-auto sm:-ml-8 sm:h-40"
            />
            <span className="app-txt-display block whitespace-nowrap">
              신뢰할 수 있는 통합 대회 플랫폼
            </span>
          </h1>
          {/* break-keep — 한글이 단어 중간에서 끊기지 않게 어절 단위로 넘긴다 */}
          <p className="app-txt-lead mt-6 max-w-2xl break-keep text-app-secondary-700">
            복싱, MMA, 킥복싱, 무에타이, 주짓수부터 전통 민속경기 닭싸움까지,
            다양한 격투스포츠 공식 대회를 한 곳에서 통합 운영합니다.
          </p>

          {/* 버튼과 지표를 한 줄에 두어 히어로 아래쪽을 좌우로 채운다 */}
          <div className="mt-12 flex flex-col gap-10 border-t border-white/10 pt-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/tournaments"
                className="focus-ring app-btn app-btn-lg app-btn-primary"
              >
                참가 신청하기
              </Link>
              <Link
                to="/bracket"
                className="focus-ring app-btn app-btn-lg border-app-text-100 text-app-text-100 hover:bg-app-text-100 hover:text-app-secondary-900"
              >
                대진표 조회
              </Link>
            </div>

            <ul className="flex list-none flex-wrap gap-x-12 gap-y-6 sm:gap-x-16">
              {homeStats.map((stat) => (
                <li key={stat.label}>
                  <p className="app-txt-h1 text-app-primary-400">
                    {stat.value}
                  </p>
                  <p className="app-txt-caption mt-1.5 tracking-[0.08em] text-app-secondary-700">
                    {stat.label}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="app-page">
        <HomeLogoMarquee />

        <div className="app-card mt-14 border-l-4 border-l-app-primary-500">
          <p className="app-txt-eyebrow text-app-primary-600">UPCOMING</p>

          {!isLoaded && <Loading label="대회 정보를 불러오는 중" />}

          {isLoaded && errorMessage && (
            <p
              role="status"
              aria-live="polite"
              className="app-txt-body2 mt-3 text-app-text-200"
            >
              {errorMessage}
            </p>
          )}

          {isLoaded && !errorMessage && !featuredTournament && (
            <p
              role="status"
              aria-live="polite"
              className="app-txt-body2 mt-3 text-app-text-200"
            >
              등록된 대회가 없습니다.
            </p>
          )}

          {isLoaded && featuredTournament && (
            <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="app-txt-h2">
                  <Link
                    to={`/tournaments/${featuredTournament.id}`}
                    className="focus-ring hover:text-app-primary-600"
                  >
                    {featuredTournament.name}
                  </Link>
                </h2>
                <p className="app-txt-body2 mt-2 text-app-text-200">
                  {featuredTournament.periodText} · {featuredTournament.venue} ·{" "}
                  {featuredTournament.statusLabel}
                </p>
              </div>

              {featuredTournament.isEntryOpen && (
                <Link
                  to={`/application?tournamentId=${featuredTournament.id}`}
                  className="focus-ring app-btn app-btn-primary shrink-0"
                >
                  참가신청
                </Link>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  )
}
